import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

type ErrorFallbackProps = {
	error: unknown;
	resetError: () => void;
};

function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
	const { t } = useTranslation();
	const message = error instanceof Error ? error.message : String(error);

	return (
		<div
			role="alert"
			className="flex min-h-svh w-full items-center justify-center bg-background p-6 md:p-10"
		>
			<div className="flex max-w-md flex-col items-center text-center">
				<div className="mb-6 flex size-20 items-center justify-center rounded-2xl bg-destructive/10">
					<AlertTriangle
						aria-hidden="true"
						className="size-10 text-destructive"
					/>
				</div>
				<h1 className="mb-3 text-2xl font-semibold tracking-tight text-foreground">
					{t("errorBoundary.title")}
				</h1>
				<p className="mb-8 text-sm leading-relaxed text-muted-foreground">
					{t("errorBoundary.description")}
				</p>
				{import.meta.env.DEV && message && (
					<pre className="mb-6 max-w-full overflow-auto rounded-md bg-muted p-3 text-left text-xs text-muted-foreground">
						{message}
					</pre>
				)}
				<div className="flex gap-2">
					<Button
						onClick={() => {
							resetError();
						}}
					>
						<RefreshCw aria-hidden="true" className="size-4" />
						{t("errorBoundary.retry")}
					</Button>
					<Button
						variant="outline"
						onClick={() => {
							window.location.reload();
						}}
					>
						{t("errorBoundary.reload")}
					</Button>
				</div>
			</div>
		</div>
	);
}

type ErrorBoundaryProps = {
	children: ReactNode;
	fallback?: (props: ErrorFallbackProps) => ReactElement;
};

export function ErrorBoundary({ children, fallback }: ErrorBoundaryProps) {
	return (
		<Sentry.ErrorBoundary
			fallback={({ error, resetError }) => {
				const props = { error, resetError };
				return fallback ? fallback(props) : <ErrorFallback {...props} />;
			}}
		>
			{children}
		</Sentry.ErrorBoundary>
	);
}
